
/**
 * This component injects page metadata based on Bloomreach data.
 * It uses Nuxt's composition API for SSR and reactivity.
 */
import {
  defineComponent,
  useContext,
  onMounted,
  watch,
} from "@nuxtjs/composition-api";
import { onSSR, sharedRef } from "@vue-storefront/core";

export default defineComponent({
  props: ["page"],
  render: () => null,
  head() {
    return {
      title: this.title || this.fallbackTitle,
      meta: this.allMetaTags,
      link: [...this.canonicals, ...this.slugTranslations],
    };
  },
  setup(props) {
    const { i18n, $config } = useContext();

    const title = sharedRef(null, "title");
    const fallbackTitle = sharedRef(null, "fallbackTitle");
    const fallbackDescription = sharedRef(null, "fallbackDescription");
    const pageMeta = sharedRef(null, "pageMeta");
    const seoMeta = sharedRef([], "seoMeta");
    const openGraphTitle = sharedRef(null, "openGraphTitle");
    const openGraphImage = sharedRef(null, "openGraphImage");
    const openGraphDescription = sharedRef(null, "openGraphDescription");
    const canonicals = sharedRef([], "canonicals");
    const slugTranslations = sharedRef([], "slugTranslations");
    const allMetaTags = sharedRef([], "allMetaTags");

    // Method to reset meta values to fallback or defaults
    const resetMeta = () => {
      if(title) {
        title.value = fallbackTitle.value;
        allMetaTags.value = [
          {
            name: "description",
            content: fallbackDescription.value,
            hid: "description",
          },
        ];
        canonicals.value = [];
        slugTranslations.value = [];
        openGraphTitle.value = {
          name: "og:title",
          content: fallbackTitle.value,
          hid: "og:title",
        };
        openGraphDescription.value = {
          name: "og:description",
          content: fallbackDescription.value,
          hid: "og:description",
        };
        openGraphImage.value = null;
      }
    };

    const getSlugsAfterPagesAndReturnUrl = (url, locale) => {
      const pagesIndex = url.indexOf("/pages");
      const hostNameWithSlug = `${$config.productionURL}/${locale.replace(
        "_",
        "-"
      )}`;

      if (pagesIndex === -1) {
        return hostNameWithSlug;
      }
      const pathAfterPages = url.substring(pagesIndex + "/pages".length);
      return `${hostNameWithSlug}${pathAfterPages}` || hostNameWithSlug;
    };

    const processPageMeta = () => {
      // Reset metadata to default values if no CMS translations or page-level meta overrides (e.g., PDP head()) are available.
      if (!props?.page?.model?.translations) {
        resetMeta();
        return;
      }

      const configuredLocales = i18n.locales.map((locale) =>
        locale.iso.toLowerCase()
      );
      const translations = props.page.model.translations.filter((translation) =>
        configuredLocales.includes(translation.locale.toLowerCase())
      );

      slugTranslations.value = translations.map((t) => {
        let urlLocale = t.locale;
        
        if (urlLocale.toLowerCase() === "en_us") {
          urlLocale = "int"; // International sites URL convention
        }

        return {
          rel: "alternate",
          hid: `i18n-alt-${t.locale}`,
          href: getSlugsAfterPagesAndReturnUrl(t.links.self.href, urlLocale),
          hreflang: urlLocale
        };
      });


      pageMeta.value = props.page?.getDocument()?.model.data || {};
      title.value = pageMeta.value?.title || fallbackTitle.value;

      seoMeta.value =
        pageMeta.value?.seometa?.map((m) => {
          if (m.tag_name.toLowerCase() === "title")
            fallbackTitle.value = m.content;
          if (m.tag_name.toLowerCase() === "description")
            fallbackDescription.value = m.content;
          return {
            name: m.tag_name.toLowerCase(),
            content: m.content,
            hid: m.tag_name.toLowerCase(),
          };
        }) || [];

      openGraphTitle.value = {
        name: "og:title",
        content:
          pageMeta?.value?.openGraphTitle?.content || fallbackTitle.value,
        hid: "og:title",
      };

      openGraphDescription.value = {
        name: "og:description",
        content:
          pageMeta?.value?.openGraphDescription?.content ||
          fallbackDescription.value,
        hid: "og:description",
      };

      if (pageMeta.value?.openGraphImage?.image) {
        const imageRef = pageMeta.value.openGraphImage.image.$ref;
        const imageSet = props.page.getContent(imageRef);
        const imageUrl = imageSet?.getOriginal().getUrl();
        openGraphImage.value = {
          name: "og:image",
          content: imageUrl,
          hid: "og:image",
        };
      }

      allMetaTags.value = [
        ...seoMeta.value,
        openGraphTitle.value,
        openGraphImage.value,
        openGraphDescription.value,
      ].filter(Boolean);

      canonicals.value = pageMeta.value?.canonicalurl
        ? [
            {
              rel: "canonical",
              hid: "canonical",
              href: pageMeta.value.canonicalurl,
            },
          ]
        : [];
    };

    onSSR(processPageMeta);
    onMounted(processPageMeta); // for client-side hydration.

    // Watch the page prop to ensure metadata is updated on route change
    watch(() => props.page, processPageMeta, { immediate: true });

    return {
      title,
      allMetaTags,
      canonicals,
      slugTranslations,
    };
  },
});
